// src/tareas/tableroTareasPersonal.jsx
import React, { useEffect, useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Global } from "../helpers/Global";
import ListadoTareasCreadas from "./listadoTareasCreadas";

const ESTADOS = [
  { id: "pendiente", titulo: "Pendiente" },
  { id: "en_curso", titulo: "En curso" },
  { id: "resuelta", titulo: "Resuelta" },
  { id: "rechazada", titulo: "Rechazada" },
];

const Toast = ({ type = "info", children }) => {
  if (!children) return null;
  const base = {
    position: "fixed",
    top: 80,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 9999,
    padding: "10px 16px",
    borderRadius: 8,
    fontWeight: 600,
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
    maxWidth: "90vw",
    textAlign: "center",
  };
  const variants = {
    ok: {
      background: "#e7f8ed",
      color: "#0f5132",
      border: "1px solid #b6f0c6",
    },
    error: {
      background: "#fdeceb",
      color: "#842029",
      border: "1px solid #f5c2c7",
    },
    info: {
      background: "#e8f6ff",
      color: "#084298",
      border: "1px solid #b6d9ff",
    },
  };
  return <div style={{ ...base, ...(variants[type] || {}) }}>{children}</div>;
};

const authHeadersJson = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// ---- Avatares ----
const getAvatarUrl = (usuario) => {
  if (!usuario?.avatar_url) return null;
  return `${Global.url}${usuario.avatar_url}`;
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();
  const n1 = nombre ? nombre.split(" ")[0][0] : "";
  const a1 = apellidos ? apellidos.split(" ")[0][0] : "";
  const combo = (n1 + a1).toUpperCase();
  if (combo) return combo;
  if (nombre) return nombre[0].toUpperCase();
  return "?";
};

// ---- Fechas ----
const formatearFechaCompleta = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const infoFechaVencimiento = (iso) => {
  if (!iso) return { texto: "", clase: "" };

  const fin = new Date(iso);
  const hoy = new Date();
  const msPorDia = 1000 * 60 * 60 * 24;
  const diff = Math.floor((fin - hoy) / msPorDia);

  if (Number.isNaN(diff)) return { texto: iso, clase: "" };

  if (diff < 0) {
    const dias = Math.abs(diff);
    const texto =
      dias === 0
        ? "Vencida"
        : `Vencida hace ${dias} día${dias === 1 ? "" : "s"}`;
    return { texto, clase: "badge-fecha--critica" };
  }
  if (diff === 0) return { texto: "Vence hoy", clase: "badge-fecha--warning" };
  if (diff === 1)
    return { texto: "Vence mañana", clase: "badge-fecha--warning" };
  if (diff <= 7)
    return { texto: `Vence en ${diff} días`, clase: "badge-fecha--warning" };

  return { texto: `Vence en ${diff} días`, clase: "badge-fecha--ok" };
};

export const TableroTareasPersonal = ({
  urlPersonalizada = null,
  mostrarTareasCreadas = true,
}) => {
  const [tareasPorEstado, setTareasPorEstado] = useState({});
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);

  const [usuariosMapa, setUsuariosMapa] = useState({});

  // Modal historial (nuevo)
  const [histModalAbierto, setHistModalAbierto] = useState(false);
  const [histCargando, setHistCargando] = useState(false);
  const [histEventos, setHistEventos] = useState([]);
  const [peticionSeleccionada, setPeticionSeleccionada] = useState(null);

  // Desplegable "tareas creadas por mí"
  const [mostrarListadoTareasCreadas, setMostrarListadoTareasCreadas] =
    useState(false);

  const mostrarToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const inicializarEstado = (items) => {
    const base = {};
    ESTADOS.forEach((e) => (base[e.id] = []));
    (items || []).forEach((item) => {
      const estado = ESTADOS.some((e) => e.id === item.estado)
        ? item.estado
        : "pendiente";
      base[estado].push(item);
    });
    setTareasPorEstado(base);
  };

  // Cargar tablero personal
  useEffect(() => {
    const fetchKanban = async () => {
      try {
        const endpoint = urlPersonalizada || `${Global.url}tareas/tablero`;
        const res = await fetch(endpoint, { headers: authHeaders() });
        const data = await res.json().catch(() => []);
        if (!res.ok) {
          console.error("Error al cargar tablero:", data);
          mostrarToast("error", data.message || "No se pudo cargar el tablero");
          inicializarEstado([]);
          return;
        }
        inicializarEstado(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error al cargar tablero:", e);
        mostrarToast("error", "Error de red al cargar el tablero");
        inicializarEstado([]);
      } finally {
        setCargando(false);
      }
    };
    fetchKanban();
  }, [urlPersonalizada]);

  // Cargar usuarios (para avatares/nombres)
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const res = await fetch(`${Global.url}usuarios/todos`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => []);
        if (res.ok && Array.isArray(data)) {
          const mapa = {};
          data.forEach((u) => {
            mapa[u.id] = u;
          });
          setUsuariosMapa(mapa);
        }
      } catch (e) {
        console.error("Error cargando usuarios para avatares:", e);
      }
    };
    fetchUsuarios();
  }, []);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const peticionId = Number(draggableId);
    const nuevoEstado = destination.droppableId;

    setTareasPorEstado((prev) => {
      const copy = {};
      ESTADOS.forEach((e) => (copy[e.id] = [...(prev[e.id] || [])]));

      const origenArr = copy[source.droppableId];
      const destinoArr = copy[destination.droppableId];

      const [movida] = origenArr.splice(source.index, 1);
      if (!movida) return prev;

      movida.estado = nuevoEstado;
      destinoArr.splice(destination.index, 0, movida);

      return copy;
    });

    try {
      const res = await fetch(
        `${Global.url}tareas/peticiones/${peticionId}/estado`,
        {
          method: "PATCH",
          headers: authHeadersJson(),
          body: JSON.stringify({ estado: nuevoEstado }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Error al actualizar estado:", data);
        mostrarToast(
          "error",
          data.message || "No se pudo actualizar el estado"
        );
      } else {
        mostrarToast("ok", "Estado actualizado");
      }
    } catch (e) {
      console.error("Error al actualizar estado:", e);
      mostrarToast("error", "Error de red al actualizar el estado");
    }
  };

  // ---------- Historial ----------
  const abrirHistorial = async (tareaPeticion) => {
    const peticionId = tareaPeticion.peticion_id;
    setPeticionSeleccionada(tareaPeticion);
    setHistEventos([]);
    setHistCargando(true);
    setHistModalAbierto(true);

    try {
      const res = await fetch(`${Global.url}peticiones/${peticionId}/historico`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        console.error("Error al cargar historial:", data);
        mostrarToast("error", data.message || "No se pudo cargar el historial");
        setHistEventos([]);
        return;
      }
      setHistEventos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar historial:", e);
      mostrarToast("error", "Error de red al cargar el historial");
      setHistEventos([]);
    } finally {
      setHistCargando(false);
    }
  };

  const cerrarHistorial = () => {
    setHistModalAbierto(false);
    setPeticionSeleccionada(null);
    setHistEventos([]);
    setHistCargando(false);
  };

  const columnas = useMemo(() => ESTADOS, []);

  if (cargando) return <p>Cargando tablero…</p>;

  return (
    <div className="kanban-wrapper">
      {toast && <Toast type={toast.type}>{toast.text}</Toast>}

      <h2 className="centrar-titulo">Tareas asignadas a mí</h2>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="kanban-board">
          {columnas.map((col) => (
            <div key={col.id} className="kanban-column">
              <div className="kanban-column-header">
                <span>{col.titulo}</span>
                <span className="kanban-column-count">
                  {(tareasPorEstado[col.id] || []).length}
                </span>
              </div>

              <Droppable droppableId={col.id}>
                {(provided, snapshot) => (
                  <div
                    className={`kanban-column-body${
                      snapshot.isDraggingOver ? " kanban-column-body--active" : ""
                    }`}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {(tareasPorEstado[col.id] || []).map((tarea, index) => {
                      const creador = usuariosMapa[tarea.creador_id];
                      const infoFecha = infoFechaVencimiento(
                        tarea.fecha_vencimiento
                      );

                      const departamentoNombre =
                        tarea.departamento_nombre || null;

                      const canalLabel =
                        tarea.tipo_destino === "departamento"
                          ? departamentoNombre
                            ? `Petición vía departamento (${departamentoNombre})`
                            : "Petición vía departamento"
                          : "Petición directa a ti";

                      return (
                        <Draggable
                          key={tarea.peticion_id}
                          draggableId={String(tarea.peticion_id)}
                          index={index}
                        >
                          {(providedDraggable, snapshotDraggable) => (
                            <div
                              className={`kanban-card${
                                snapshotDraggable.isDragging
                                  ? " kanban-card--dragging"
                                  : ""
                              }`}
                              ref={providedDraggable.innerRef}
                              {...providedDraggable.draggableProps}
                              {...providedDraggable.dragHandleProps}
                            >
                              {/* CABECERA: avatar + nombre en fila */}
                              <div className="kanban-card-header">
                                <div className="kanban-card-avatar">
                                  {creador && getAvatarUrl(creador) ? (
                                    <img
                                      src={getAvatarUrl(creador)}
                                      alt={`Avatar de ${creador.nombre || "usuario"}`}
                                    />
                                  ) : (
                                    <div className="avatar-fallback avatar-fallback--small">
                                      {getIniciales(creador || {})}
                                    </div>
                                  )}
                                </div>
                                <div className="kanban-card-header-text">
                                  <span className="kanban-card-creator-name">
                                    {creador
                                      ? `${creador.nombre || ""} ${
                                          creador.apellidos || ""
                                        }`.trim()
                                      : "Creador desconocido"}
                                  </span>
                                  <span className="kanban-card-creator-label">
                                    Creador
                                  </span>
                                </div>
                              </div>

                              {/* Título */}
                              <div className="kanban-card-title">
                                {tarea.titulo}
                              </div>

                              {/* Departamento asociado (tag) */}
                              {departamentoNombre && (
                                <div className="kanban-card-depto">
                                  <span className="kanban-depto-tag">
                                    {departamentoNombre}
                                  </span>
                                </div>
                              )}

                              {/* Descripción */}
                              {tarea.descripcion && (
                                <div className="kanban-card-desc">
                                  {tarea.descripcion}
                                </div>
                              )}

                              {/* Meta */}
                              <div className="kanban-card-meta">
                                <span
                                  className={`badge-prioridad badge-prioridad--${
                                    tarea.prioridad || "normal"
                                  }`}
                                >
                                  {tarea.prioridad || "normal"}
                                </span>

                                {tarea.fecha_vencimiento && infoFecha.texto && (
                                  <span
                                    className={`badge-fecha ${infoFecha.clase}`}
                                    title={formatearFechaCompleta(
                                      tarea.fecha_vencimiento
                                    )}
                                  >
                                    ⏰ {infoFecha.texto}
                                  </span>
                                )}
                              </div>

                              {/* Footer 1: responsable */}
                              <div className="kanban-card-footer">
                                <span className="kanban-destino">
                                  Responsable: tú
                                </span>
                              </div>

                              {/* Footer 2: canal/contexto (como etiqueta inferior) */}
                              <div className="kanban-card-footer">
                                <span className="kanban-destino">
                                  {canalLabel}
                                </span>
                              </div>

                              <div className="kanban-card-actions">
                                <button
                                  type="button"
                                  className="kanban-card-history-btn"
                                  onClick={() => abrirHistorial(tarea)}
                                >
                                  Ver historial
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* --------- DESPLEGABLE: Tareas creadas por mí --------- */}
      {mostrarTareasCreadas && (
        <div className="tareas-creadas-desplegable">
          <button
            type="button"
            className="btn-toggle-desplegable"
            onClick={() => setMostrarListadoTareasCreadas((v) => !v)}
          >
            {mostrarListadoTareasCreadas
              ? "Ocultar tareas creadas por mí"
              : "Tareas creadas por mí"}
          </button>

          {mostrarListadoTareasCreadas && (
            <div className="tareas-creadas-desplegable-body">
              <ListadoTareasCreadas embebido />
            </div>
          )}
        </div>
      )}

      {/* MODAL HISTORIAL (mismo que departamento, por clases CSS) */}
      {histModalAbierto && (
        <div className="modal-overlay modal-historial-overlay">
          <div className="modal-content modal-historial-contenido">
            <button
              className="modal-close historial-close-btn"
              onClick={cerrarHistorial}
            >
              ✕
            </button>

            <h3 className="centrar-titulo">
              Historial de petición #{peticionSeleccionada?.peticion_id}
            </h3>

            <p style={{ marginBottom: 16 }}>
              <strong>Tarea:</strong> {peticionSeleccionada?.titulo}
            </p>

            <div className="historial-scroll">
              {histCargando ? (
                <p>Cargando historial…</p>
              ) : histEventos.length === 0 ? (
                <p style={{ fontStyle: "italic" }}>
                  No hay histórico registrado para esta petición.
                </p>
              ) : (
                histEventos.map((ev) => {
                  const usuarioNombre = `${ev.modificado_por_nombre || ""} ${
                    ev.modificado_por_apellidos || ""
                  }`.trim();

                  const hayCambioAsignacion =
                    ev.asignado_anterior_id !== null ||
                    ev.asignado_nuevo_id !== null;

                  const asignadoAnteriorNombre =
                    ev.asignado_anterior_nombre || ev.asignado_anterior_apellidos
                      ? `${ev.asignado_anterior_nombre || ""} ${
                          ev.asignado_anterior_apellidos || ""
                        }`.trim()
                      : "Sin responsable";

                  const asignadoNuevoNombre =
                    ev.asignado_nuevo_nombre || ev.asignado_nuevo_apellidos
                      ? `${ev.asignado_nuevo_nombre || ""} ${
                          ev.asignado_nuevo_apellidos || ""
                        }`.trim()
                      : "Sin responsable";

                  return (
                    <div key={ev.id} className="historial-item-card">
                      <div className="historial-header">
                        <div className="avatar-small">
                          {usuarioNombre ? usuarioNombre.charAt(0) : "?"}
                        </div>
                        <div className="historial-header-text">
                          <span className="historial-user">
                            {usuarioNombre || "Usuario desconocido"}
                          </span>
                          <span className="historial-date">
                            {formatearFechaCompleta(ev.fecha_cambio)}
                          </span>
                        </div>
                      </div>

                      <div className="historial-body">
                        {hayCambioAsignacion ? (
                          <span>
                            cambió la asignación de{" "}
                            <strong>{asignadoAnteriorNombre}</strong> a{" "}
                            <strong>{asignadoNuevoNombre}</strong>.
                          </span>
                        ) : (
                          <span>
                            cambió el estado de{" "}
                            <strong>{ev.estado_anterior}</strong> a{" "}
                            <strong>{ev.estado}</strong>.
                          </span>
                        )}

                        {ev.comentario && (
                          <div className="historial-comentario">
                            «{ev.comentario}»
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableroTareasPersonal;
